var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { faCloudUploadAlt, faFile, faTrashAlt } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Link, Typography, styled, } from '@mui/material';
import moment from 'moment';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import { MAX_FILE_SIZE } from './constants';
import { defaultFormValues } from './createPlan';
var UploadBox = styled(Box)(function (_a) {
    var theme = _a.theme;
    return ({
        border: "0.1rem dashed ".concat(theme.palette.text.primary),
        borderRadius: 1,
        padding: 10,
        textAlign: 'center',
    });
});
var FileNameBox = styled(Box)(function (_a) {
    var theme = _a.theme;
    return ({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        border: "1px solid ".concat(theme.palette.grey[500]),
        borderRadius: 1,
        marginTop: 10,
        padding: 5,
    });
});
export var UploadTemplateDialog = function (_a) {
    var open = _a.open, onClose = _a.onClose, handleUploadComplete = _a.handleUploadComplete, uploadedFile = _a.uploadedFile, setUploadedFile = _a.setUploadedFile, setValue = _a.setValue;
    var t = useTranslation(['myPlans', 'translation']).t;
    var _b = useState(), rejectFile = _b[0], setRejectFile = _b[1];
    var _c = useState(defaultFormValues), excelValues = _c[0], setExcelValues = _c[1];
    var inputFile = useRef(null);
    useEffect(function () {
        if (!uploadedFile && inputFile.current) {
            var dataTransfer = new DataTransfer();
            inputFile.current.files = dataTransfer.files;
        }
    }, [uploadedFile]);
    var validateFile = useCallback(function (uploadFile) {
        if (!uploadFile)
            return;
        var reader = new FileReader();
        reader.onload = function (e) {
            var _a, _b, _c, _d;
            var arrayBuffer = (_a = e.target) === null || _a === void 0 ? void 0 : _a.result;
            var workbook = XLSX.read(arrayBuffer, { type: 'array' });
            var sheetName = workbook.SheetNames[1];
            var worksheet = workbook.Sheets[sheetName];
            var jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 'A', raw: false });
            var date = moment.utc((_b = jsonData[3]) === null || _b === void 0 ? void 0 : _b.B, 'MM DD YYYY');
            var uploadPlanType = (_c = jsonData[5]) === null || _c === void 0 ? void 0 : _c.B;
            var uploadPeriodLength = Number((_d = jsonData[5]) === null || _d === void 0 ? void 0 : _d.F);
            if (date.isValid() && uploadPlanType && uploadPeriodLength) {
                setUploadedFile(uploadFile);
                setExcelValues(__assign(__assign({}, excelValues), { startDate: date, planType: uploadPlanType, periodLength: uploadPeriodLength }));
            }
            else {
                var errors = [{ code: 'invalid-excel-values', message: 'Invalid excel values' }];
                setRejectFile({ file: uploadFile, errors: errors });
            }
        };
        reader.readAsArrayBuffer(uploadFile);
    }, [excelValues, setUploadedFile]);
    var formatFileSize = function (bytes) {
        if (bytes < 1024) {
            return bytes + ' bytes';
        }
        else if (bytes < 1024 * 1024) {
            return (bytes / 1024).toFixed(2) + ' KB';
        }
        return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
    };
    var onDrop = useCallback(function (acceptedFiles, rejectedFiles) {
        setUploadedFile(undefined);
        setRejectFile(rejectedFiles[0]);
        validateFile(acceptedFiles[0]);
    }, [setUploadedFile, validateFile]);
    var _d = useDropzone({
        accept: {
            'application/vnd.ms-excel': ['.xls'],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
        },
        maxSize: MAX_FILE_SIZE,
        onDrop: onDrop,
        noClick: true,
        noKeyboard: true,
    }), getRootProps = _d.getRootProps, getInputProps = _d.getInputProps, isDragActive = _d.isDragActive;
    var handleClose = function () {
        onClose();
        setRejectFile(undefined);
    };
    var handleDoneClick = function () {
        if (!uploadedFile)
            return;
        setValue('startDate', excelValues.startDate, { shouldValidate: true });
        setValue('planType', excelValues.planType);
        setValue('periodLength', excelValues.periodLength, { shouldValidate: true });
        handleUploadComplete();
    };
    var handleBrowseClick = function () {
        var _a;
        (_a = inputFile.current) === null || _a === void 0 ? void 0 : _a.click();
    };
    var getFileUploadErrorMessage = function () {
        if ((rejectFile === null || rejectFile === void 0 ? void 0 : rejectFile.errors[0].code) === 'file-invalid-type') {
            return _jsx("span", { children: t('upload.invalidFileType') });
        }
        if ((rejectFile === null || rejectFile === void 0 ? void 0 : rejectFile.errors[0].code) === 'file-too-large') {
            return _jsx("span", { children: t('upload.invalidSize') });
        }
        return _jsx("span", { children: t('upload.invalidFileFormat') });
    };
    return (_jsxs(Dialog, __assign({ open: open, onClose: handleClose, disablePortal: true }, { children: [_jsx(DialogTitle, __assign({ variant: "h2" }, { children: t('upload.uploadFile') })), _jsxs(DialogContent, __assign({ sx: { paddingBottom: 0 } }, { children: [_jsxs(UploadBox, __assign({}, getRootProps(), { children: [_jsx("input", __assign({ "data-testid": "drop-input" }, getInputProps(), { accept: ".xls,.xlsx", ref: inputFile })), _jsx(FontAwesomeIcon, { icon: faCloudUploadAlt, size: "2x" }), isDragActive ? (_jsx(Typography, { children: t('upload.dropFileHere') })) : (_jsxs(Typography, { children: [_jsx(Link, __assign({ "data-testid": "link-btn", component: "button", onClick: handleBrowseClick }, { children: t('upload.browse') })), t('upload.orDragAndDropHere')] })), _jsx(Typography, __assign({ color: "text.secondary", variant: "body2" }, { children: t('upload.sizeLimit') })), _jsx(Typography, __assign({ color: "text.secondary", variant: "body2" }, { children: t('upload.fileType') }))] })), uploadedFile && (_jsxs(FileNameBox, { children: [_jsxs(Box, __assign({ sx: { display: 'flex', alignItems: 'center' } }, { children: [_jsx(Box, { children: _jsx(FontAwesomeIcon, { icon: faFile, size: "xl" }) }), _jsxs(Box, __assign({ ml: 2 }, { children: [_jsx(Typography, __assign({ variant: "body2" }, { children: uploadedFile.name })), _jsx(Typography, __assign({ variant: "body2" }, { children: formatFileSize(uploadedFile.size) }))] }))] })), _jsx(Link, __assign({ component: IconButton, size: "small", onClick: function () { return setUploadedFile(undefined); } }, { children: _jsx(FontAwesomeIcon, { icon: faTrashAlt }) }))] })), rejectFile && (_jsx(Typography, __assign({ mt: 3, color: "error.dark", variant: "body2" }, { children: getFileUploadErrorMessage() })))] })), _jsxs(DialogActions, { children: [_jsx(Button, __assign({ "data-testid": "cancel-btn", variant: "outlined", onClick: handleClose }, { children: t('translation:action.cancel') })), _jsx(Button, __assign({ "data-testid": "done-btn", variant: "contained", type: "submit", onClick: handleDoneClick, disabled: !uploadedFile }, { children: t('translation:action.done') }))] })] })));
};
